import { ImoClassAutoCompleteProps } from '@tom-ui/planning'
import { PATHS, useAsyncFetch, useNativeNavigationGuard } from '@tom-ui/utils'
import { useYardStores } from '@yard/AppProvider'
import { ReactNode, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { CarrierVisitAllocationRulesViewStore } from '../stores/allocation-rules/carrier-visit-allocation-rules.view-store'
import { GlobalAllocationRulesViewStore } from '../stores/allocation-rules/global-allocation-rules.view-store'
import { CarrierVisitOverviewViewStore } from '../stores/carrier-visit-overview.viewstore'
import { HousekeepingPlanAccumulatorViewStore } from '../stores/housekeeping-plan-accumulator.view-store'
import { YardSetupViewStore } from '../stores/yard-setup.view-store'
import { YardViewStore } from '../stores/yard.view-store'

interface UseYardSetupProps {
  getJourneyByContainerIds: (ids: number[]) => Promise<void>
  companyAutoComplete: (props: any) => ReactNode
  locationCodeAutoComplete: (props: any) => ReactNode
  imoClassAutoComplete: (props: ImoClassAutoCompleteProps) => ReactNode
  transportSelection: (props: any) => ReactNode
}

export const useYardSetup = ({
  companyAutoComplete,
  locationCodeAutoComplete,
  imoClassAutoComplete,
  transportSelection,
  getJourneyByContainerIds,
}: UseYardSetupProps) => {
  const {
    carrierVisitStore,
    occupancyStore,
    yardBlockStore,
    layoutObjectStore,
    berthStore,
    railTrackStore,
    pierStore,
    allocationRuleStore,
    journeyStore,
    roadNetworkStore,
    capacitySettingsStore,
  } = useYardStores()

  const location = useLocation()

  const setupStore = useMemo(
    () =>
      new YardSetupViewStore(
        yardBlockStore,
        layoutObjectStore,
        berthStore,
        railTrackStore,
        pierStore,
        occupancyStore,
        roadNetworkStore,
      ),
    [
      yardBlockStore,
      layoutObjectStore,
      berthStore,
      railTrackStore,
      pierStore,
      occupancyStore,
      roadNetworkStore,
    ],
  )

  const globalAllocationRulesViewStore = useMemo(
    () =>
      new GlobalAllocationRulesViewStore(
        allocationRuleStore,
        occupancyStore,
        journeyStore,
        yardBlockStore,
        capacitySettingsStore,
      ),
    [allocationRuleStore, occupancyStore, journeyStore, yardBlockStore, capacitySettingsStore],
  )

  const carrierVisitAllocationRulesViewStore = useMemo(
    () =>
      new CarrierVisitAllocationRulesViewStore(
        allocationRuleStore,
        occupancyStore,
        journeyStore,
        carrierVisitStore,
        yardBlockStore,
        capacitySettingsStore,
      ),
    [
      allocationRuleStore,
      occupancyStore,
      journeyStore,
      carrierVisitStore,
      yardBlockStore,
      capacitySettingsStore,
    ],
  )

  const carrierVisitOverviewViewStore = useMemo(
    () => new CarrierVisitOverviewViewStore(carrierVisitStore, journeyStore, allocationRuleStore),
    [carrierVisitStore, journeyStore, allocationRuleStore],
  )

  const housekeepingPlanAccumulatorViewStore = useMemo(
    () => new HousekeepingPlanAccumulatorViewStore(),
    [],
  )

  const viewStore = useMemo(
    () =>
      new YardViewStore(
        layoutObjectStore,
        occupancyStore,
        yardBlockStore,
        berthStore,
        railTrackStore,
        globalAllocationRulesViewStore,
        carrierVisitAllocationRulesViewStore,
        carrierVisitOverviewViewStore,
        journeyStore,
        transportSelection,
        companyAutoComplete,
        locationCodeAutoComplete,
        imoClassAutoComplete,
        getJourneyByContainerIds,
      ),
    [
      layoutObjectStore,
      occupancyStore,
      yardBlockStore,
      berthStore,
      railTrackStore,
      globalAllocationRulesViewStore,
      carrierVisitAllocationRulesViewStore,
      carrierVisitOverviewViewStore,
      journeyStore,
      transportSelection,
      getJourneyByContainerIds,
      companyAutoComplete,
      locationCodeAutoComplete,
      imoClassAutoComplete,
    ],
  )

  useEffect(() => {
    viewStore.setIsViewMode(location.pathname === PATHS.yard.view.url)
  }, [location.pathname, viewStore])

  const { loading } = useAsyncFetch(async () => {
    await Promise.all([
      carrierVisitStore.loadCarrierVisits(),
      journeyStore.loadOpenJourneys(),
      yardBlockStore.loadYardBlocks(),
      layoutObjectStore.loadLayoutObjects(),
      berthStore.loadBerths(),
      railTrackStore.loadRailTracks(),
      pierStore.loadPiers(),
      occupancyStore.loadOccupancies(),
      allocationRuleStore.loadNonTemplateRules(),
      roadNetworkStore.loadAll(),
    ])
  }, [
    carrierVisitStore,
    journeyStore,
    yardBlockStore,
    layoutObjectStore,
    berthStore,
    railTrackStore,
    pierStore,
    occupancyStore,
    allocationRuleStore,
    roadNetworkStore,
  ])

  useEffect(() => {
    globalAllocationRulesViewStore.setAllocationRules(allocationRuleStore.globalRules)
  }, [allocationRuleStore.globalRules, globalAllocationRulesViewStore])

  // `Yard` (the sole consumer) is an `observer`, so reading these observables directly
  // preserves reactivity. Wrapping them in `computed(...).get()` would allocate a fresh
  // computed every render and defeat memoization without changing behavior.
  const isLeftDrawerOpen: boolean =
    globalAllocationRulesViewStore.isAllocationRulesDrawerOpen ||
    carrierVisitAllocationRulesViewStore.isAllocationRulesDrawerOpen ||
    carrierVisitOverviewViewStore.isOverviewOpen ||
    housekeepingPlanAccumulatorViewStore.isAccumulatorOpen

  const shouldQueryBeforePageLeave: boolean =
    viewStore.isViewMode &&
    (globalAllocationRulesViewStore.hasUnsavedChanges ||
      carrierVisitAllocationRulesViewStore.hasUnsavedChanges)

  useNativeNavigationGuard(shouldQueryBeforePageLeave)

  return {
    loading,
    viewStore,
    setupStore,
    globalAllocationRulesViewStore,
    carrierVisitAllocationRulesViewStore,
    carrierVisitOverviewViewStore,
    housekeepingPlanAccumulatorViewStore,
    allocationRuleStore,
    isLeftDrawerOpen,
  }
}
